import { useParams, Link } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePhenotype } from '@/hooks/use-phenotypes'
import { useOrganization } from '@/contexts/organization-context'
import { ExamplesTab } from '@/components/phenotypes/ExamplesTab'
import { CodeGenTab } from '@/components/phenotypes/CodeGenTab'
import { VersionHistoryTab } from '@/components/phenotypes/VersionHistoryTab'
import { ExtractorPlayground } from '@/components/phenotypes/ExtractorPlayground'
import { STATUS_MAP } from '@/components/phenotypes/PhenotypeCard'

export function PhenotypeDetail() {
  const { id } = useParams<{ id: string }>()
  const { selectedOrgId } = useOrganization()
  const { data: phenotype, isLoading } = usePhenotype(id, selectedOrgId || undefined)

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="h-4 w-full bg-muted animate-pulse rounded" />
              <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Not found
  if (!phenotype) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Link to="/phenotypes">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Phenotype Not Found</h1>
        </div>
        <p className="text-muted-foreground">
          Could not find phenotype with ID: {id}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/phenotypes">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{phenotype.carrierName}</h1>
            {(() => {
              const detailStatus = STATUS_MAP[phenotype.extractorStatus ?? 'none'] ?? STATUS_MAP.none
              return <Badge variant={detailStatus.variant}>{detailStatus.label}</Badge>
            })()}
          </div>
          <p className="text-muted-foreground font-mono text-sm mt-1">{id}</p>
        </div>
      </div>

      {/* Phenotype info */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Carrier ID</p>
              <p className="font-mono text-sm">{phenotype.carrierId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Detection Threshold</p>
              <p className="text-sm font-medium">{Math.round(phenotype.detectionThreshold * 100)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Extractions</p>
              <p className="text-sm font-medium">{phenotype.statistics?.extractionCount ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Avg Confidence</p>
              <p className="text-sm font-medium">
                {phenotype.statistics?.averageConfidence
                  ? `${Math.round(phenotype.statistics.averageConfidence * 100)}%`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Examples + Code Generation + Version History */}
      <Tabs defaultValue="examples" className="w-full">
        <TabsList className="inline-flex">
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="codegen">Code Generation</TabsTrigger>
          <TabsTrigger value="playground">Playground</TabsTrigger>
          <TabsTrigger value="versions">Version History</TabsTrigger>
        </TabsList>
        <TabsContent value="examples" className="mt-6">
          {id && <ExamplesTab phenotypeId={id} />}
        </TabsContent>
        <TabsContent value="codegen" className="mt-6">
          {id && <CodeGenTab phenotypeId={id} />}
        </TabsContent>
        <TabsContent value="playground" className="mt-6">
          {id && <ExtractorPlayground phenotypeId={id} />}
        </TabsContent>
        <TabsContent value="versions" className="mt-6">
          {id && <VersionHistoryTab phenotypeId={id} />}
        </TabsContent>
      </Tabs>
    </div>
  )
}
